import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCategories, getMenuItems, getCoupons } from "../api/api";
import { useCart } from "../context/CartContext";
import Carousel from "../components/home/Carousel";
import QuickActions from "../components/home/QuickActions";
import CategoryList from "../components/home/CategoryList";
import MenuGrid from "../components/home/MenuGrid";
import CouponList from "../components/home/CouponList";
import LoadingSpinner from "../components/home/LoadingSpinner";
import "../styles/home.css";
import { useParams } from "react-router-dom";

export default function Home() {
  const navigate = useNavigate();
  const { addToCart, getCartCount, setTableNo } = useCart();
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { tableId } = useParams();

  useEffect(() => {
    if (tableId) {
      localStorage.setItem("tableNo", tableId);
      setTableNo(tableId);
    }
  }, [tableId, setTableNo]);

  const banners = [
    {
      title: "🔥 Pizza Fiesta",
      desc: "Flat 30% OFF on all pizzas",
      img: "https://images.unsplash.com/photo-1601924582970-9238bcb495d9",
      color: "#ff6b6b",
      badge: "Limited Time",
    },
    {
      title: "🍔 Burger Mania",
      desc: "Buy 1 Get 1 Free today",
      img: "https://images.unsplash.com/photo-1550547660-d9450f859349",
      color: "#ffa502",
      badge: "Best Deal",
    },
    {
      title: "🥤 Cold Coffee Deal",
      desc: "Starts at just ₹99",
      img: "https://images.unsplash.com/photo-1509042239860-f550ce710b93",
      color: "#1e90ff",
      badge: "New Arrival",
    },
  ];

  const quickActions = [
    { name: "Menu", icon: "📖", color: "#ff6b6b", action: "/category" },
    { name: "Offers", icon: "🎁", color: "#ffa502", action: "/offers" },
    {
      name: "Cart",
      icon: "🛒",
      color: "#0baf9a",
      action: "/cart",
      badge: getCartCount(),
    },
    { name: "Help", icon: "💬", color: "#4a69bd", action: "help" },
  ];

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [categoriesData, menuData, couponsData] = await Promise.all([
          getCategories(),
          getMenuItems(),
          getCoupons(),
        ]);

        console.log("menuData:", menuData);

        const items = Array.isArray(menuData)
          ? menuData
          : menuData?.data || menuData?.menu_items || [];

        setCategories(categoriesData || []);
        setMenuItems(items);
        setFilteredItems(items);

        console.log("COUPONS API:", couponsData);

        setCoupons(couponsData.coupons || couponsData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Filter menu items when category changes
  useEffect(() => {
    const filterItemsByCategory = async () => {
      try {
        if (selectedCategory === "all") {
          const allItems = await getMenuItems();
          setFilteredItems(allItems || []);
        } else {
          const filtered = await getMenuItems(selectedCategory);
          setFilteredItems(filtered || []);
        }
      } catch (error) {
        console.error("Error filtering items:", error);
        setFilteredItems([]);
      }
    };
    filterItemsByCategory();
  }, [selectedCategory]);

  const handleAddToCart = (item) => {
    addToCart(item, 1, null);
    // Optional: Show a quick notification
    const notification = document.createElement("div");
    notification.className = "cart-notification";
    notification.textContent = `✓ ${item.item_name} added to cart`;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  };

  const handleQuickAction = (action) => {
    if (action === "help") {
      alert("Need help? Contact us at support@table7.com");
    } else if (action.startsWith("/")) {
      navigate(action);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Connection Error</h2>
          <p>{error}</p>
          <p className="error-hint">
            Make sure your Laravel backend is running on{" "}
            <strong>http://127.0.0.1:8000</strong>
          </p>
          <button
            onClick={() => window.location.reload()}
            className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button className="scroll-top" onClick={scrollToTop}>
          ↑
        </button>
      )}

      {/* Carousel Section */}
      <Carousel banners={banners} />

      {/* Quick Actions */}
      <QuickActions actions={quickActions} onActionClick={handleQuickAction} />

      {/* Categories Section */}
      <CategoryList
        categories={categories}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
      />

      {/* Menu Items Grid */}
      <MenuGrid
        items={(filteredItems || []).slice(0, 3)}
        onAddToCart={handleAddToCart}
        title={selectedCategory === "all" ? "Popular Items" : "Top Picks"}
        subtitle={
          selectedCategory === "all"
            ? "Customer favorites this week"
            : "Best items from this category"
        }
      />

      <CouponList coupons={coupons} />
    </div>
  );
}
